import React from 'react';

const isValidUrl = (url: string): boolean => {
  const urlPattern =
    /^(https?:\/\/)?(www\.)?(?!.*--)([a-zA-Z0-9]+(-[a-zA-Z0-9]+)*\.)+([a-zA-Z]{2,})(:[0-9]+)?(\/.*)?$/;

  return urlPattern.test(url);
};

// To linkify text in chat messages inside threads
const linkifyText = (
  text: string,
  linkClassName: string = 'underline cursor-pointer'
): React.ReactNode[] => {
  const urlRegex =
    /(?:https?:\/\/|www\.)[^\s<>"'{}|\\^`\[\]]+|\b([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}(?:\/[^\s]*)?\b/g; // This checks for any link sent inside threads

  let lastIndex = 0;
  const result: React.ReactNode[] = [];
  let match: RegExpExecArray | null;
  let keyIndex = 0;

  while ((match = urlRegex.exec(text)) !== null) {
    const matchStart = match.index;
    const matchEnd = matchStart + match[0].length;
    const url = match[0].trim();

    if (matchStart > lastIndex) {
      const textPart = text.slice(lastIndex, matchStart);
      if (textPart) {
        result.push(textPart);
      }
    }

    if (isValidUrl(url)) {
      let href = url;

      if (!/^https?:\/\//.test(url)) {
        href = `https://${url.replace(/^www\./, '')}`;
      }

      result.push(
        React.createElement(
          'a',
          {
            key: `link-${keyIndex++}`,
            href,
            target: '_blank',
            rel: 'noopener noreferrer',
            className: linkClassName,
            style: {
              color: '#026EB9',
              textDecoration: 'underline',
            },
            onMouseEnter: (e: React.MouseEvent<HTMLAnchorElement>) => {
              e.currentTarget.style.color = '#005A9C';
            },
            onMouseLeave: (e: React.MouseEvent<HTMLAnchorElement>) => {
              e.currentTarget.style.color = '#026EB9';
            },
            onClick: (e: React.MouseEvent) => e.stopPropagation(),
          },
          url
        )
      );
    } else {
      result.push(url);
    }

    lastIndex = matchEnd;
  }

  // Add remaining text after the last URL
  if (lastIndex < text.length) {
    const remainingText = text.slice(lastIndex);
    if (remainingText) {
      result.push(remainingText);
    }
  }

  return result;
};

// We are using memoize to stop processing the same text messages whenever it re-renders
const createMemoizedLinkify = () => {
  const cache = new Map<string, React.ReactNode[]>();
  const MAX_CACHE_SIZE = 100;

  return (
    text: string,
    linkClassName: string = 'underline cursor-pointer'
  ): React.ReactNode[] => {
    const cacheKey = `${text}-${linkClassName}`;

    if (cache.has(cacheKey)) {
      return cache.get(cacheKey)!;
    }

    const result = linkifyText(text, linkClassName);

    if (cache.size >= MAX_CACHE_SIZE) {
      const firstKey = cache.keys().next().value;
      cache.delete(firstKey);
    }

    cache.set(cacheKey, result);
    return result;
  };
};

function toTitleCaseWord(word: string) {
  return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
}

function toTitleCaseSentence(line: string) {
  return line
    .split(' ')
    .map((word) => toTitleCaseWord(word))
    .join(' ');
}

/**
 * Title-cases a sentence but leaves already-all-uppercase words untouched, so
 * acronyms survive (e.g. `QST registration number` → `QST Registration Number`,
 * not `Qst Registration Number`).
 */
function toTitleCasePreservingAcronyms(line: string) {
  return line
    .split(' ')
    .map((word) =>
      word.length > 1 && word === word.toUpperCase() ? word : toTitleCaseWord(word)
    )
    .join(' ');
}

/**
 * Builds an identifier-safe slug from free text: drops accents, lower-cases, collapses every run of
 * non-alphanumerics into a single `-`, and trims separators off both ends
 * (e.g. `Intercompany Agreements` → `intercompany-agreements`).
 */
export function slugify(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^[-.]+|[-.]+$/g, '');
}

/** Turns underscore-separated enum/API identifiers into Title Case labels (e.g. `BALANCE_SHEET` → `Balance Sheet`). */
export function enumKeyToDisplayName(value: string): string {
  return value
    .trim()
    .replaceAll('_', ' ')
    .split(/\s+/)
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export {
  createMemoizedLinkify,
  isValidUrl,
  linkifyText,
  toTitleCasePreservingAcronyms,
  toTitleCaseSentence,
};
